import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Fields,
    SingleSelect,
    MultiSelectCreatable,
    HorizontalFlex,
    FieldLabel,
    ButtonWithIconSecondary,
    icons,
} from 'smartsuite-ui';
import _ from 'lodash';
import { predicateValues, errorTimeout } from '../constants/constants';
import { useLabelContext } from '../hooks/labelContext';
import { getPredicateOptions, buildFilterWarning } from './filtersModalUtils';
import validateInputAgainstFieldRegex from './regexValidatorUtil';

/* eslint-disable complexity */
export default function FilterUnconstrainedMultiSelect(props) {
    const { dashboardLabels, predicateLabels, translationLabels } =
        useLabelContext();

    function getRequiredError(values) {
        return props.isRequired && _.isEmpty(values)
            ? dashboardLabels.required
            : null;
    }

    const filterValues = props.filter?.values ?? [];
    const [errorMessage, setErrorMessage] = useState(
        getRequiredError(filterValues)
    );
    const [errorTimeoutId, setErrorTimeoutId] = useState();

    const predicateOptions = getPredicateOptions(
        predicateLabels,
        props.availableFilter.predicateOptions
    );
    const placeholder =
        props.filter?.predicate?.valueField === predicateValues.notIn
            ? translationLabels.none
            : translationLabels.all;

    function handleFilterPredicateChange(predicate) {
        props.updateFilter(
            {
                [props.filterKey]: {
                    values: [],
                    ...props.filter,
                    predicate,
                },
            },
            props.availableFilter.cascadeChildKey
        );
    }

    function dismissErrorMessage(values) {
        setErrorMessage(getRequiredError(values));
        setErrorTimeoutId(null);
    }

    function validateNewValue(value) {
        const isValueValid = validateInputAgainstFieldRegex(
            value,
            props.availableFilter.fieldRegex
        );

        if (isValueValid) {
            return true;
        }

        const timeoutId = setTimeout(
            () => dismissErrorMessage(filterValues),
            errorTimeout
        );

        setErrorMessage(`"${value}" ${dashboardLabels.isInvalid}`);
        setErrorTimeoutId(timeoutId);
        return false;
    }

    function handleFilterValueChange(values) {
        dismissErrorMessage(values);

        const updatedFilterValue = {
            [props.filterKey]: {
                predicate: predicateOptions[0],
                ...props.filter,
                values: values ?? [],
            },
        };

        if (!values?.length) {
            props.updateFilter(
                updatedFilterValue,
                props.availableFilter.cascadeChildKey
            );
            return;
        }

        const newestValue = values[values.length - 1];
        if (validateNewValue(newestValue.valueField)) {
            props.updateFilter(
                updatedFilterValue,
                props.availableFilter.cascadeChildKey
            );
        }
    }

    function handleCreate(createdOption) {
        if (errorTimeoutId) {
            clearTimeout(errorTimeoutId);
        }

        const updatedFilterValues = [
            ...filterValues,
            { textField: createdOption, valueField: createdOption },
        ];

        handleFilterValueChange(updatedFilterValues);
    }

    function renderFilterLabel() {
        const label =
            props.showAsterisk && props.isRequired
                ? `${props.availableFilter.label}*`
                : props.availableFilter.label;

        if (!props.isAdditionalFilter) {
            return label;
        }

        return (
            <HorizontalFlex>
                <FieldLabel>{label}</FieldLabel>
                <ButtonWithIconSecondary
                    iconClass={icons.x}
                    onClick={props.removeAdditionalFilter(props.filterKey)}
                />
            </HorizontalFlex>
        );
    }

    return (
        <Fields
            cssClassModifiers={[
                'no-background',
                'no-pad-top-bottom',
                'no-wrap',
                props.isEditor ? 'column' : '',
            ]}
        >
            <SingleSelect
                disabled={props.disabled}
                isClearable={false}
                label={renderFilterLabel()}
                menuPortalTarget={props.isEditor ? document.body : null}
                onChange={handleFilterPredicateChange}
                options={predicateOptions}
                styles={{
                    menuPortal: (styles) => ({
                        ...styles,
                        zIndex: 10,
                    }),
                }}
                textField="textField"
                valueField="valueField"
                value={props.filter?.predicate ?? predicateOptions[0]}
            />
            <MultiSelectCreatable
                disabled={props.disabled}
                isValid={!errorMessage}
                errorMessage={errorMessage}
                handleCreate={handleCreate}
                menuPortalTarget={props.isEditor ? document.body : null}
                onChange={handleFilterValueChange}
                options={props.options}
                placeholder={placeholder}
                styles={{
                    menuPortal: (styles) => ({
                        ...styles,
                        zIndex: 10,
                    }),
                }}
                textField="textField"
                valueField="valueField"
                value={filterValues}
            />
            {props.availableFilter.appliesToDataSubset && (
                <div className="filter-warning-icon">
                    <span
                        className={icons.warningFilled}
                        title={buildFilterWarning(
                            props.availableFilter.label,
                            dashboardLabels
                        )}
                    />
                </div>
            )}
        </Fields>
    );
}
/* eslint-enable complexity */

FilterUnconstrainedMultiSelect.propTypes = {
    availableFilter: PropTypes.instanceOf(Object).isRequired,
    disabled: PropTypes.bool.isRequired,
    filter: PropTypes.instanceOf(Object),
    filterKey: PropTypes.string.isRequired,
    isAdditionalFilter: PropTypes.bool.isRequired,
    isEditor: PropTypes.bool,
    isRequired: PropTypes.bool.isRequired,
    options: PropTypes.instanceOf(Array),
    removeAdditionalFilter: PropTypes.func.isRequired,
    showAsterisk: PropTypes.bool.isRequired,
    updateFilter: PropTypes.func.isRequired,
};

FilterUnconstrainedMultiSelect.defaultProps = {
    filter: {},
    options: [],
    isEditor: false,
};
